// views/Dashboard/Investments/Investments.tsx
import { useMemo, useState } from 'react';
import Card from '../../../components/common/Card/Card';
import styles from './Investments.module.css';

const PIE_URL = 'https://www.trading212.com/pies/luakyuJayjlsRjchA03BSTwOkcoUE';

// Sdílená oranžová paleta (barva výseče = index v koláči).
const PALETTE = ['#e37627', '#f79038', '#f9b168', '#c85f16', '#8a4410'];

interface Holding {
  name: string;
  ticker: string;
  allocation: number; // percent
}

const PIE_DESCRIPTION =
  'Ryze akciové portfolio s pákovými ETF — nejvyšší růstový potenciál i kolísavost.';

const HOLDINGS: Holding[] = [
  { name: 'Avantis Global Small Cap Value (Acc)', ticker: 'AVWS', allocation: 35 },
  { name: 'Invesco MSCI World Equal Weight (Acc)', ticker: 'MWEQ', allocation: 30 },
  { name: 'Vanguard FTSE All-World (Acc)', ticker: 'VWCE', allocation: 20 },
  { name: 'Xtrackers S&P 500 2x Leveraged Daily Swap (Acc)', ticker: 'DBPG', allocation: 10 },
  { name: 'WisdomTree S&P 500 3x Daily Leveraged', ticker: '3USL', allocation: 5 },
];

const RADIUS = 80;
const STROKE = 34;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const Investments = () => {
  const [active, setActive] = useState<number | null>(null);

  // Předpočítej segmenty donutu (offset po obvodu kruhu).
  const segments = useMemo(() => {
    let cumulative = 0;
    return HOLDINGS.map((h, i) => {
      const dash = (h.allocation / 100) * CIRCUMFERENCE;
      const offset = -(cumulative / 100) * CIRCUMFERENCE;
      cumulative += h.allocation;
      return { ...h, color: PALETTE[i % PALETTE.length], dash, offset };
    });
  }, []);

  const focused = active !== null ? HOLDINGS[active] : null;

  return (
    <div className={styles.investments}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Investice</h1>
          <p className={styles.subtitle}>
            Inspirativní portfolio ALGAPIA sestavené jako koláč na Trading&nbsp;212.
          </p>
        </div>
      </header>

      <p className={styles.pieDesc}>{PIE_DESCRIPTION}</p>

      <div className={styles.grid}>
        <Card className={styles.chartCard} padding="large">
          <div className={styles.chartWrap}>
            <svg viewBox="0 0 200 200" className={styles.donut} role="img" aria-label="Rozložení portfolia">
              <g transform="rotate(-90 100 100)">
                {segments.map((seg, i) => (
                  <circle
                    key={seg.ticker}
                    cx="100"
                    cy="100"
                    r={RADIUS}
                    fill="none"
                    stroke={seg.color}
                    strokeWidth={active === i ? STROKE + 6 : STROKE}
                    strokeDasharray={`${seg.dash} ${CIRCUMFERENCE - seg.dash}`}
                    strokeDashoffset={seg.offset}
                    className={styles.segment}
                    style={{ opacity: active === null || active === i ? 1 : 0.35 }}
                    onMouseEnter={() => setActive(i)}
                    onMouseLeave={() => setActive(null)}
                  />
                ))}
              </g>
            </svg>
            <div className={styles.center}>
              {focused ? (
                <>
                  <span className={styles.centerPct}>{focused.allocation}%</span>
                  <span className={styles.centerLabel}>{focused.ticker}</span>
                </>
              ) : (
                <>
                  <span className={styles.centerPct}>{HOLDINGS.length}</span>
                  <span className={styles.centerLabel}>výsečí</span>
                </>
              )}
            </div>
          </div>
        </Card>

        <Card className={styles.legendCard} padding="large">
          <div className={styles.legendHead}>
            <h2 className={styles.legendTitle}>Výseče</h2>
            <a className={styles.sourceLink} href={PIE_URL} target="_blank" rel="noopener noreferrer">
              Otevřít na Trading 212 ↗
            </a>
          </div>
          <ul className={styles.legend}>
            {segments.map((seg, i) => (
              <li
                key={seg.ticker}
                className={`${styles.legendItem} ${active === i ? styles.legendActive : ''}`}
                onMouseEnter={() => setActive(i)}
                onMouseLeave={() => setActive(null)}
              >
                <span className={styles.dot} style={{ background: seg.color }} />
                <span className={styles.legendName}>
                  {seg.name}
                  <span className={styles.legendTicker}>{seg.ticker}</span>
                </span>
                <span className={styles.legendPct}>{seg.allocation}%</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <Card className={styles.lowRiskCard} padding="large">
        <h2 className={styles.lowRiskTitle}>Hledáte jinou možnost?</h2>
        <p className={styles.lowRiskText}>
          Pokud chcete investovat jinde než přes Trading&nbsp;212 nebo se vám tento koláč prostě nelíbí,
          můžete se podívat například na <strong>Vanguard FTSE All-World (Acc)</strong> (VWCE) — jeden
          široce diverzifikovaný globální akciový fond, který jedním nákupem drží{' '}
          <strong>více než 3&nbsp;600 akcií</strong> z rozvinutých i rozvíjejících se trhů po celém světě.
        </p>
        <p className={styles.lowRiskText}>
          Nejde o naše doporučení — nejsme finanční poradci a neposkytujeme investiční poradenství.
          Slouží to jen jako inspirace, konečné rozhodnutí je vždy na vás.
        </p>
      </Card>

      <p className={styles.disclaimer}>
        Uvedené portfolio slouží pouze jako inspirace a není investičním doporučením. Hodnota investice
        může kolísat a návratnost není zaručena.
      </p>
    </div>
  );
};

export default Investments;
